/**
 * useCompleteSession hook
 * Manages session completion with React Query mutations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { completeSession } from '@/services/sessions';

/**
 * Return type for useCompleteSession hook
 */
export interface UseCompleteSessionReturn {
  /** Function to complete the session */
  complete: () => void;

  /** True while completion is in progress */
  isCompleting: boolean;

  /** Error object if completion failed */
  error: Error | null;

  /** True after successful completion */
  isSuccess: boolean;
}

/**
 * Hook to complete a tutoring session
 *
 * @param sessionId - The session ID to complete
 * @returns Object with complete function and state flags
 */
export function useCompleteSession(sessionId: string): UseCompleteSessionReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => completeSession(sessionId),

    onSuccess: () => {
      // Invalidate session messages to update status
      queryClient.invalidateQueries({
        queryKey: ['session', sessionId, 'messages'],
      });

      // Invalidate sessions list to reflect completion
      queryClient.invalidateQueries({
        queryKey: ['sessions'],
      });

      // Invalidate user profile so pebbles display refreshes (AC-003.5 / US-003)
      queryClient.invalidateQueries({
        queryKey: ['user', 'profile'],
      });

      // Invalidate math coach active session cache so guidance screen shows on next visit (US-006)
      // Safe for English sessions — query key won't match any cached data for those
      queryClient.invalidateQueries({
        queryKey: ['math-coach', 'active'],
      });
    },
  });

  return {
    complete: () => mutation.mutate(),
    isCompleting: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
  };
}
